import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';

const GridBg = () => (
  <div className="absolute inset-0 grid-texture pointer-events-none" aria-hidden="true" />
);

export function SampleMemo() {
  return (
    <div className="bg-zinc-950">
      <SEOHead />
      <section className="relative section-border pt-10 pb-6 md:pt-12 md:pb-8 overflow-hidden">
        <GridBg />
        <div className="container-site relative z-10 max-w-3xl">
          <div className="border-b border-zinc-800 pb-12 mb-12">
            <span className="text-xs font-mono tracking-widest uppercase text-amber-500 block mb-5">Sample Document</span>
            <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-zinc-100 leading-tight">Diagnostics Preview</h1>
            <p className="mt-6 text-base text-zinc-400 max-w-xl leading-relaxed">
              The executive summary below outlines the structure and format of a typical OnPoint Diagnostics deliverable.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 border border-amber-500/20 bg-amber-500/5 rounded">
              <span className="text-xs font-mono text-amber-300">Sample Artifact (Synthetic Data Illustration)</span>
            </div>
          </div>

          {/* Memo document */}
          <div className="border border-zinc-800 rounded-md overflow-hidden">
            {/* Header */}
            <div className="bg-zinc-900 border-b border-zinc-800 px-10 py-8">
              <div className="text-xs font-mono tracking-widest uppercase text-amber-500 mb-1">Analytical Memorandum</div>
              <div className="font-heading text-xl font-medium text-zinc-100">OnPoint Diagnostics Findings</div>
              <div className="mt-5 grid grid-cols-2 gap-y-2 text-xs font-mono">
                <div><span className="text-zinc-600 mr-2">TO:</span><span className="text-zinc-300">[Engagement Delivery Email]</span></div>
                <div><span className="text-zinc-600 mr-2">FROM:</span><span className="text-zinc-300">OnPoint.Works | WFM Analytics Lab</span></div>
                <div><span className="text-zinc-600 mr-2">DATE:</span><span className="text-zinc-300">[Engagement Delivery Date]</span></div>
                <div><span className="text-zinc-600 mr-2">REF:</span><span className="text-zinc-300">RD-2025-XXXX</span></div>
              </div>
            </div>

            {/* Body */}
            <div className="p-10 bg-zinc-950 space-y-8">
              {/* Summary */}
              <div>
                <div className="text-[10px] font-mono tracking-widest uppercase text-zinc-600 border-b text-amber-500 pb-2 mb-4">EXECUTIVE SUMMARY</div>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  Classification of the submitted interval-level WFM data identifies 
                  <strong className="text-zinc-200"> Forecast Decomposition Failure</strong> as the dominant distortion domain (Confidence Index: 72). Schedule Architecture Distortion (58) and Intraday Signal Collapse (44) function as secondary interaction drivers, amplifying the primary structural exposure. Detailed findings are presented in Sections I-IV and Annexes A-C.
                </p>
              </div>

              {/* I */}
              <div>
                <div className="text-[10px] font-mono tracking-widest uppercase text-zinc-600 border-b text-amber-500 pb-2 mb-4">I. PRIMARY DISTORTION DOMAIN</div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs font-mono text-amber-500 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded">Domain I</span>
                  <span className="text-sm font-medium text-zinc-200">Forecast Decomposition Failure</span>
                  <span className="text-xs font-mono text-zinc-500">CI: 72</span>
                </div>
                <p className="text-sm text-zinc-400 leading-relaxed mb-3">
                  Systematic pattern decomposition error generates persistent interval bias during the 09:00-11:30 and 14:00-15:30 intervals. The bias is statistically nonrandom (CV = 0.12, below the 0.20 randomness threshold).
                </p>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  A pattern error has been differentiated from the volume component. Volume alone yields forecast accuracy of 4.2% MAPE. The distortion is therefore located in pattern shape.
                </p>
              </div>

              {/* II */}
              <div>
                <div className="text-[10px] font-mono tracking-widest uppercase text-zinc-600 border-b text-amber-500 pb-2 mb-4">II. SECONDARY INTERACTION DRIVERS</div>
                <div className="space-y-4">
                  {[
                    { id: 'II-A', domain: 'Schedule Architecture Distortion', ci: 58, desc: 'Shift boundary concentrations at 09:45 and 14:15 shift start intervals create coverage gaps that overlap with the primary distortion domain.' },
                    { id: 'II-B', domain: 'Intraday Signal Collapse', ci: 44, desc: 'Reforecast accuracy declines after 10:30 in the operating day. By 13:00 the reforecast spread is already on average 18% from the actuals and nullifies the correction window that would otherwise cover the Domain I exposure' },
                  ].map((item) => (
                    <div key={item.id} className="pl-5 border-l-2 border-zinc-800">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs font-mono text-zinc-600">{item.id}</span>
                        <span className="text-sm font-medium text-zinc-300">{item.domain}</span>
                        <span className="text-xs font-mono text-zinc-500">CI: {item.ci}</span>
                      </div>
                      <p className="text-sm text-zinc-400 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* III */}
              <div>
                <div className="text-[10px] font-mono tracking-widest uppercase text-zinc-600 border-b text-amber-500 pb-2 mb-4">III. SIGNAL STRUCTURE AND EXPOSURE MAPPING</div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {[
                    { k: 'Recurrence frequency', v: '4.2x / operating week' },
                    { k: 'Sensitivity index', v: '0.71 (High)' },
                    { k: 'Affected intervals / week', v: '14–17 of 96 total' },
                    { k: 'Primary queue exposure', v: 'Queue 03, 07 (see Annex A)' },
                  ].map((row) => (
                    <div key={row.k} className="border-b border-zinc-800 pb-2">
                      <div className="text-[10px] font-mono text-zinc-600 mb-0.5">{row.k}</div>
                      <div className="text-xs text-zinc-300">{row.v}</div>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  Signal shape appears constant throughout the entire 90 day period. See Annex B (FIG 1.0) for interval intensity heatmap and FIG 2.0 for residual series.
                </p>
              </div>

              {/* IV */}
              <div>
                <div className="text-[10px] font-mono tracking-widest uppercase text-zinc-600 border-b text-amber-500 pb-2 mb-4">IV. BOUNDED NEXT-STEP OPTIONS</div>
                <p className="text-sm text-zinc-500 italic mb-4">
                  The options below are presented for organizational consideration.
This summary does not prescribe implementation
                </p>
                <div className="space-y-3">
                  {[
                    { opt: 'A', label: 'Extended Forecast Decomposition (Module A)', desc: 'Suitable when pattern separation analysis is necessary to investigate the pattern decomposition failure mechanism.' },
                    { opt: 'B', label: 'Internal remediation', desc: 'Accept current classification and direct internal remediation effort work focused on the forecast architecture. This summary provides sufficient characterization to brief internal WFM leadership.' },
                    { opt: 'C', label: 'No further action', desc: 'Classification is complete. Findings documented. No obligation to proceed.' },
                  ].map((item) => (
                    <div key={item.opt} className="grid grid-cols-[7rem_1fr] gap-5">
                      <span className="text-xs font-mono text-amber-500 mt-0.5">Option {item.opt}</span>
                      <div>
                        <div className="text-sm font-medium text-zinc-300">{item.label}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-zinc-800 pt-6">
                <p className="text-[10px] font-mono text-zinc-600">
                  This memorandum is confidential and intended solely for the recipient organization. Submitted data will be permanently deleted 14 days from this delivery date. Engagement reference: RD-2025-XXXX.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex gap-4">
            <Link to="/intake" data-testid="sample-memo-intake-cta" className="inline-flex items-center gap-2 px-6 py-3 bg-zinc-100 text-zinc-900 rounded-md text-sm font-medium hover:bg-white transition-colors duration-200">
              Begin Intake <ArrowRight size={14} />
            </Link>
            <Link to="/demo" className="inline-flex items-center gap-2 px-6 py-3 border border-zinc-700 text-zinc-300 rounded-md text-sm hover:border-zinc-500 hover:text-white transition-colors duration-200">
              View Artifact Gallery
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
